import { ReactElement, ReactNode } from 'react';

import { capitalize, styled } from '@mui/material';
import MUIAlert, { AlertProps as MUIAlertProps } from '@mui/material/Alert';
import MUIAlertTitle from '@mui/material/AlertTitle';
import clsx from 'clsx';

import { Sizes, SxProps } from '../../types';
import { ButtonContext, ButtonProps } from '../buttons';
import {
  CheckCircle,
  IconContext,
  Laptop,
  ShieldWarning,
  Warning,
  WarningCircle,
  X,
  XCircle,
} from '../icons';

type Color =
  | 'success'
  | 'info'
  | 'warning'
  | 'error'
  | 'advisory'
  | 'exceptional';

export type AlertProps = Omit<MUIAlertProps, 'color' | 'icon'> & {
  size?: Sizes<'sm' | 'lg'>;
  icon?: ReactElement | Color;
  noIcon?: boolean;
  color?: Color;
  testId?: string;
  titleSx?: SxProps;
  colorToIconMap?: Partial<
    Record<NonNullable<AlertProps['color']>, ReactElement>
  >;
};

const buttonColors: Partial<
  Record<NonNullable<AlertProps['color']>, ButtonProps['variant']>
> = {
  advisory: 'primary',
  info: 'primary',
  success: 'primary',
  warning: 'danger',
  error: 'danger',
  exceptional: 'primary',
};

const alertIconMap = {
  advisory: <ShieldWarning />,
  info: <WarningCircle />,
  success: <CheckCircle />,
  warning: <Warning />,
  error: <XCircle />,
  exceptional: <Laptop />,
};
const StyledAlert = styled(MUIAlert, {
  name: 'DsAlert',
  slot: 'Root',
  target: 'DsAlert-root',
  overridesResolver: ({ ownerState }, theme) => {
    return [
      theme.root,
      theme[ownerState.dsVariant + capitalize(ownerState.dsColor)],
      theme[ownerState.dsSize],
      ownerState.noIcon ? theme.noIcon : {},
    ];
  },
})<{
  ownerState: {
    dsVariant: string;
    dsColor: string;
    dsSize: string;
    noIcon?: boolean;
  };
}>({});

export const Alert = ({
  title,
  size = 'lg',
  children,
  className,
  noIcon,
  icon,
  color = 'exceptional',
  variant = 'filled',
  testId,
  colorToIconMap = alertIconMap,
  titleSx,
  ...props
}: AlertProps) => {
  if (colorToIconMap !== alertIconMap) {
    colorToIconMap = { ...alertIconMap, ...colorToIconMap };
  }
  let iconEl: ReactNode;
  if (!noIcon) {
    if (icon) {
      iconEl = typeof icon === 'string' ? colorToIconMap[icon] : icon;
    } else if (color) {
      iconEl = colorToIconMap[color];
    }
  }
  return (
    <IconContext.Provider value={{ size: '1em', weight: 'fill' }}>
      <ButtonContext.Provider
        value={{
          size: size === 'sm' ? 'sm' : 'md',
          variant: color && buttonColors[color],
        }}
      >
        <StyledAlert
          data-testid={testId}
          ownerState={{
            dsVariant: variant,
            dsColor: color,
            dsSize: size,
            noIcon,
          }}
          className={clsx(className, !!children && 'DsAlert-hasChildren')}
          icon={iconEl}
          action={
            props.onClose && (
              <X
                className="DsAlert-close"
                size={size === 'sm' ? '16px' : '20px'}
                weight="bold"
                onClick={props.onClose}
                opacity={0.4}
                data-testid="close-btn"
              />
            )
          }
          variant={variant}
          {...props}
        >
          {title && <MUIAlertTitle sx={titleSx}>{title}</MUIAlertTitle>}
          {children && <div className="DsAlert-content">{children}</div>}
        </StyledAlert>
      </ButtonContext.Provider>
    </IconContext.Provider>
  );
};
